'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  Search,
  Activity,
  Shield,
  Wallet,
  Settings,
  ChevronLeft,
  ChevronRight,
  Zap,
  Server,
  Layers,
  ShieldCheck,
  Cpu,
  GitMerge,
  Database,
  Wifi,
  BookOpen,
  KeyRound,
  WalletCards,
  ShieldAlert,
  Network,
  Radio,
  GitBranch,
  Code2,
  Box,
  Coins,
  CircleDollarSign,
  Globe,
  Plug,
  Code,
  Monitor,
  X,
  LayoutDashboard,
  Vote,
  FileSearch,
} from 'lucide-react';
import Icon from '@/components/ui/AppIcon';

const navGroups = [
  {
    label: 'CORE ENGINE',
    items: [
      { href: '/core-engine', label: 'Core Engine', sublabel: 'R1–R7', icon: Layers },
      { href: '/blockchain-protocol-engine', label: 'Protocol Engine', sublabel: 'P3', icon: Cpu },
      { href: '/consensus-engine', label: 'Consensus Engine', sublabel: 'P4', icon: GitMerge },
      { href: '/ledger-state-engine', label: 'Ledger & State', sublabel: 'P5', icon: Database },
      { href: '/p2p-network-engine', label: 'P2P Network', sublabel: 'P6', icon: Wifi },
      { href: '/system-registry', label: 'System Registry', sublabel: 'P6.5', icon: BookOpen },
      {
        href: '/cryptography-identity-engine',
        label: 'Cryptography & Identity',
        sublabel: 'P7',
        icon: KeyRound,
      },
      { href: '/wallet-engine', label: 'Wallet Engine', sublabel: 'P8', icon: WalletCards },
      {
        href: '/validator-infrastructure',
        label: 'Validator Infrastructure',
        sublabel: 'P9',
        icon: ShieldAlert,
      },
      {
        href: '/node-infrastructure',
        label: 'Node Infrastructure',
        sublabel: 'P10',
        icon: Network,
      },
      { href: '/rpc-platform', label: 'RPC Platform', sublabel: 'P11', icon: Radio },
      { href: '/indexer-engine', label: 'Indexer Engine', sublabel: 'P12', icon: Database },
      { href: '/event-bus', label: 'Event Bus', sublabel: 'P12.5', icon: GitBranch },
      {
        href: '/smart-contract-runtime',
        label: 'Smart Contract Runtime',
        sublabel: 'P13',
        icon: Code2,
      },
      {
        href: '/virtual-machine-sandbox',
        label: 'VM & Execution Sandbox',
        sublabel: 'P13.5',
        icon: Box,
      },
      { href: '/asset-token-engine', label: 'Asset & Token Engine', sublabel: 'P14', icon: Coins },
      {
        href: '/native-currency-engine',
        label: 'Native Currency Engine',
        sublabel: 'P14.5',
        icon: CircleDollarSign,
      },
      { href: '/explorer-platform', label: 'Explorer Platform', sublabel: 'P15', icon: Globe },
      {
        href: '/integration-framework',
        label: 'Integration Framework',
        sublabel: 'P16',
        icon: Plug,
      },
    ],
  },
  {
    label: 'DEVELOPER PLATFORM',
    items: [
      { href: '/developer-platform', label: 'Developer Platform', sublabel: 'P17', icon: Code },
    ],
  },
  {
    label: 'MONITORING',
    items: [
      {
        href: '/monitoring-observability',
        label: 'Monitoring & Observability',
        sublabel: 'P18',
        icon: Monitor,
      },
    ],
  },
  {
    label: 'GOVERNANCE',
    items: [
      { href: '/governance-runtime', label: 'Governance Runtime', sublabel: 'P20', icon: Vote },
    ],
  },
  {
    label: 'DESIGN SYSTEM',
    items: [
      {
        href: '/enterprise-design-system',
        label: 'Enterprise Design System',
        sublabel: 'P19',
        icon: LayoutDashboard,
      },
    ],
  },
  {
    label: 'ARCHITECTURE',
    items: [
      {
        href: '/architecture-validation-report',
        label: 'Validation Report',
        sublabel: 'AVR-001',
        icon: FileSearch,
      },
      { href: '/architecture-book', label: 'Architecture Book', sublabel: 'v1.0', icon: BookOpen },
    ],
  },
  {
    label: 'EXPLORE',
    items: [
      { href: '/explorer', label: 'RTWO Explorer', sublabel: 'v2.1', icon: Globe },
      { href: '/', label: 'Blockchain Explorer', sublabel: null, icon: Search },
    ],
  },
  {
    label: 'OPERATIONS',
    items: [
      {
        href: '/operations-dashboard',
        label: 'Operations Dashboard',
        sublabel: 'OP9',
        icon: LayoutDashboard,
      },
      { href: '/monitoring-dashboard', label: 'Monitoring', sublabel: null, icon: Activity },
      {
        href: '/validator-staking-management',
        label: 'Validators & Staking',
        sublabel: null,
        icon: Shield,
      },
      { href: '/governance-dashboard', label: 'Governance', sublabel: null, icon: Zap },
    ],
  },
  {
    label: 'ASSETS',
    items: [
      { href: '/wallet-asset-management', label: 'Wallet & Assets', sublabel: null, icon: Wallet },
    ],
  },
  {
    label: 'ADMINISTRATION',
    items: [
      { href: '/enterprise-admin-panel', label: 'Enterprise Admin', sublabel: null, icon: Server },
      { href: '/system-integrity', label: 'System Integrity', sublabel: null, icon: ShieldCheck },
    ],
  },
];

// Flatten all items for search
const allNavItems = navGroups.flatMap((g) => g.items.map((item) => ({ ...item, group: g.label })));

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return allNavItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        (item.sublabel && item.sublabel.toLowerCase().includes(q)) ||
        item.group.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const showSearch = searchOpen && !collapsed;

  return (
    <aside data-component-id="src/components/Sidebar.tsx:219:4" data-component-path="src/components/Sidebar.tsx" data-component-line="219" data-component-end-line="398" data-component-file="Sidebar.tsx" data-component-name="aside" data-component-content="%7B%22elementName%22%3A%22aside%22%7D"
      className={`sidebar-transition flex flex-col bg-card border-r border-border h-screen sticky top-0 z-40 ${
        collapsed ? 'w-16' : 'w-64'
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div data-component-id="src/components/Sidebar.tsx:227:6" data-component-path="src/components/Sidebar.tsx" data-component-line="227" data-component-end-line="254" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
        className={`flex items-center h-16 border-b border-border px-3 shrink-0 ${collapsed ? 'justify-center' : 'gap-3'}`}
      >
        <AppLogo data-component-id="src/components/Sidebar.tsx:230:8" data-component-path="src/components/Sidebar.tsx" data-component-line="230" data-component-end-line="230" data-component-file="Sidebar.tsx" data-component-name="AppLogo" data-component-content="%7B%22elementName%22%3A%22AppLogo%22%7D" size={32} />
        {!collapsed && (
          <div data-component-id="src/components/Sidebar.tsx:232:10" data-component-path="src/components/Sidebar.tsx" data-component-line="232" data-component-end-line="239" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20min-w-0%20flex-1%22%7D" className="flex flex-col min-w-0 flex-1">
            <span data-component-id="src/components/Sidebar.tsx:233:12" data-component-path="src/components/Sidebar.tsx" data-component-line="233" data-component-end-line="235" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-bold%20text-sm%20text-foreground%20tracking-tight%20leading-tight%22%2C%22textContent%22%3A%22RTWOChain%22%7D" className="font-bold text-sm text-foreground tracking-tight leading-tight">
              RTWOChain
            </span>
            <span data-component-id="src/components/Sidebar.tsx:236:12" data-component-path="src/components/Sidebar.tsx" data-component-line="236" data-component-end-line="238" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20tracking-widest%20uppercase%22%2C%22textContent%22%3A%22Core%20Engine%20v1.0%22%7D" className="text-2xs text-muted-foreground tracking-widest uppercase">
              Core Engine v1.0
            </span>
          </div>
        )}
        {!collapsed && (
          <button data-component-id="src/components/Sidebar.tsx:242:10" data-component-path="src/components/Sidebar.tsx" data-component-line="242" data-component-end-line="252" data-component-file="Sidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-7%20h-7%20flex%20items-center%20justify-center%20rounded-md%20text-muted-foreground%20hover%3Atext-foreground%20hover%3Abg-muted%20transition-all%20duration-150%20shrink-0%22%7D"
            onClick={() => {
              setSearchOpen((v) => !v);
              setSearchQuery('');
            }}
            className="w-7 h-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 shrink-0"
            aria-label={searchOpen ? 'Close search' : 'Search modules'}
            title="Search modules"
          >
            {searchOpen ? <X data-component-id="src/components/Sidebar.tsx:251:26" data-component-path="src/components/Sidebar.tsx" data-component-line="251" data-component-end-line="251" data-component-file="Sidebar.tsx" data-component-name="X" data-component-content="%7B%22elementName%22%3A%22X%22%7D" size={14} /> : <Search data-component-id="src/components/Sidebar.tsx:251:44" data-component-path="src/components/Sidebar.tsx" data-component-line="251" data-component-end-line="251" data-component-file="Sidebar.tsx" data-component-name="Search" data-component-content="%7B%22elementName%22%3A%22Search%22%7D" size={14} />}
          </button>
        )}
      </div>

      {/* Search */}
      {showSearch && (
        <div data-component-id="src/components/Sidebar.tsx:258:8" data-component-path="src/components/Sidebar.tsx" data-component-line="258" data-component-end-line="309" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-3%20py-2%20border-b%20border-border%20shrink-0%22%7D" className="px-3 py-2 border-b border-border shrink-0">
          <div data-component-id="src/components/Sidebar.tsx:259:10" data-component-path="src/components/Sidebar.tsx" data-component-line="259" data-component-end-line="273" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%22%7D" className="relative">
            <Search data-component-id="src/components/Sidebar.tsx:260:12" data-component-path="src/components/Sidebar.tsx" data-component-line="260" data-component-end-line="263" data-component-file="Sidebar.tsx" data-component-name="Search" data-component-content="%7B%22elementName%22%3A%22Search%22%2C%22className%22%3A%22absolute%20left-2.5%20top-1%2F2%20-translate-y-1%2F2%20text-muted-foreground%20pointer-events-none%22%7D"
              size={13}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
            />
            <input data-component-id="src/components/Sidebar.tsx:264:12" data-component-path="src/components/Sidebar.tsx" data-component-line="264" data-component-end-line="272" data-component-file="Sidebar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22search%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%2C%22className%22%3A%22search-enterprise%20text-xs%22%7D"
              type="search"
              placeholder="Search modules..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-enterprise text-xs"
              autoFocus
              aria-label="Search modules"
            />
          </div>
          {searchQuery && (
            <div data-component-id="src/components/Sidebar.tsx:275:12" data-component-path="src/components/Sidebar.tsx" data-component-line="275" data-component-end-line="307" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-2%20space-y-0.5%20max-h-48%20overflow-y-auto%22%7D" className="mt-2 space-y-0.5 max-h-48 overflow-y-auto">
              {searchResults.length === 0 ? (
                <p data-component-id="src/components/Sidebar.tsx:277:16" data-component-path="src/components/Sidebar.tsx" data-component-line="277" data-component-end-line="277" data-component-file="Sidebar.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-2xs%20text-muted-foreground%20px-2%20py-1%22%2C%22textContent%22%3A%22No%20modules%20found%22%7D" className="text-2xs text-muted-foreground px-2 py-1">No modules found</p>
              ) : (
                searchResults.map((item) => {
                  const Icon = item.icon;
                  const isActive = pathname === item.href;
                  return (
                    <Link data-component-id="src/components/Sidebar.tsx:283:20" data-component-path="src/components/Sidebar.tsx" data-component-line="283" data-component-end-line="303" data-component-file="Sidebar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                      key={`sr-${item.href}`}
                      href={item.href}
                      onClick={() => {
                        setSearchOpen(false);
                        setSearchQuery('');
                      }}
                      className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-all duration-150 ${
                        isActive
                          ? 'nav-item-active'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      <Icon data-component-id="src/components/Sidebar.tsx:296:22" data-component-path="src/components/Sidebar.tsx" data-component-line="296" data-component-end-line="296" data-component-file="Sidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22className%22%3A%22shrink-0%22%7D" size={13} className="shrink-0" />
                      <span data-component-id="src/components/Sidebar.tsx:297:22" data-component-path="src/components/Sidebar.tsx" data-component-line="297" data-component-end-line="297" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22truncate%22%7D" className="truncate">{item.label}</span>
                      {item.sublabel && (
                        <span data-component-id="src/components/Sidebar.tsx:299:24" data-component-path="src/components/Sidebar.tsx" data-component-line="299" data-component-end-line="301" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22ml-auto%20text-2xs%20text-muted-foreground%2F60%20font-mono%20shrink-0%22%7D" className="ml-auto text-2xs text-muted-foreground/60 font-mono shrink-0">
                          {item.sublabel}
                        </span>
                      )}
                    </Link>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}

      {/* Nav */}
      <nav data-component-id="src/components/Sidebar.tsx:313:6" data-component-path="src/components/Sidebar.tsx" data-component-line="313" data-component-end-line="371" data-component-file="Sidebar.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex-1%20overflow-y-auto%20sidebar-scroll%20py-3%20px-2%20space-y-4%22%7D"
        className="flex-1 overflow-y-auto sidebar-scroll py-3 px-2 space-y-4"
        aria-label="Module navigation"
      >
        {navGroups.map((group) => (
          <div data-component-id="src/components/Sidebar.tsx:318:10" data-component-path="src/components/Sidebar.tsx" data-component-line="318" data-component-end-line="369" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" key={`group-${group.label}`}>
            {!collapsed && (
              <p data-component-id="src/components/Sidebar.tsx:320:14" data-component-path="src/components/Sidebar.tsx" data-component-line="320" data-component-end-line="322" data-component-file="Sidebar.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22label-uppercase%20px-2%20mb-1.5%22%7D" className="label-uppercase px-2 mb-1.5" aria-hidden="true">
                {group.label}
              </p>
            )}
            <ul data-component-id="src/components/Sidebar.tsx:324:12" data-component-path="src/components/Sidebar.tsx" data-component-line="324" data-component-end-line="368" data-component-file="Sidebar.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22space-y-0.5%22%7D" className="space-y-0.5" role="list">
              {group.items.map((item) => {
                const isActive = pathname === item.href;
                const Icon = item.icon;
                const isCoreEngine = item.href === '/core-engine';
                return (
                  <li data-component-id="src/components/Sidebar.tsx:330:18" data-component-path="src/components/Sidebar.tsx" data-component-line="330" data-component-end-line="365" data-component-file="Sidebar.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%7D" key={`nav-${item.href}`}>
                    <Link data-component-id="src/components/Sidebar.tsx:331:20" data-component-path="src/components/Sidebar.tsx" data-component-line="331" data-component-end-line="364" data-component-file="Sidebar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                      href={item.href}
                      title={
                        collapsed
                          ? `${item.label}${item.sublabel ? ` (${item.sublabel})` : ''}`
                          : undefined
                      }
                      aria-current={isActive ? 'page' : undefined}
                      className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs font-medium transition-all duration-150 group relative ${
                        isActive
                          ? 'nav-item-active'
                          : `nav-item-inactive hover:bg-muted hover:text-foreground ${isCoreEngine ? 'border border-primary/15 bg-primary/4' : ''}`
                      } ${collapsed ? 'justify-center' : ''}`}
                    >
                      <Icon data-component-id="src/components/Sidebar.tsx:345:22" data-component-path="src/components/Sidebar.tsx" data-component-line="345" data-component-end-line="349" data-component-file="Sidebar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D"
                        size={15}
                        className={`shrink-0 ${isCoreEngine && !isActive ? 'text-primary/70' : ''}`}
                        aria-hidden="true"
                      />
                      {!collapsed && (
                        <span data-component-id="src/components/Sidebar.tsx:351:24" data-component-path="src/components/Sidebar.tsx" data-component-line="351" data-component-end-line="355" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                          className={`truncate flex-1 ${isCoreEngine && !isActive ? 'text-primary/80' : ''}`}
                        >
                          {item.label}
                        </span>
                      )}
                      {!collapsed && item.sublabel && (
                        <span data-component-id="src/components/Sidebar.tsx:358:24" data-component-path="src/components/Sidebar.tsx" data-component-line="358" data-component-end-line="362" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                          className={`text-2xs font-mono shrink-0 ${isActive ? 'text-primary/60' : 'text-muted-foreground/50'}`}
                        >
                          {item.sublabel}
                        </span>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div data-component-id="src/components/Sidebar.tsx:374:6" data-component-path="src/components/Sidebar.tsx" data-component-line="374" data-component-end-line="397" data-component-file="Sidebar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22border-t%20border-border%20p-2%20space-y-0.5%20shrink-0%22%7D" className="border-t border-border p-2 space-y-0.5 shrink-0">
        <Link data-component-id="src/components/Sidebar.tsx:375:8" data-component-path="src/components/Sidebar.tsx" data-component-line="375" data-component-end-line="383" data-component-file="Sidebar.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%23%22%7D"
          href="#"
          className={`flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          title={collapsed ? 'Settings' : undefined}
          aria-label="Settings"
        >
          <Settings data-component-id="src/components/Sidebar.tsx:381:10" data-component-path="src/components/Sidebar.tsx" data-component-line="381" data-component-end-line="381" data-component-file="Sidebar.tsx" data-component-name="Settings" data-component-content="%7B%22elementName%22%3A%22Settings%22%7D" size={15} aria-hidden="true" />
          {!collapsed && <span data-component-id="src/components/Sidebar.tsx:382:25" data-component-path="src/components/Sidebar.tsx" data-component-line="382" data-component-end-line="382" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Settings%22%7D">Settings</span>}
        </Link>
        <button data-component-id="src/components/Sidebar.tsx:384:8" data-component-path="src/components/Sidebar.tsx" data-component-line="384" data-component-end-line="396" data-component-file="Sidebar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          onClick={onToggle}
          className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight data-component-id="src/components/Sidebar.tsx:391:12" data-component-path="src/components/Sidebar.tsx" data-component-line="391" data-component-end-line="391" data-component-file="Sidebar.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%7D" size={15} aria-hidden="true" />
          ) : (
            <ChevronLeft data-component-id="src/components/Sidebar.tsx:393:12" data-component-path="src/components/Sidebar.tsx" data-component-line="393" data-component-end-line="393" data-component-file="Sidebar.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%7D" size={15} aria-hidden="true" />
          )}
          {!collapsed && <span data-component-id="src/components/Sidebar.tsx:395:25" data-component-path="src/components/Sidebar.tsx" data-component-line="395" data-component-end-line="395" data-component-file="Sidebar.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Collapse%22%7D">Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
